import {
    StringEnum,
    LoyaltyProgramMerchant,
    SearchMode,
    LoyaltyProgram,
    LoyaltyProgramCoupon,
    Experiment,
    AuthProvider,
} from '../../definitions/CommonDefinitions'
import { Flyer } from '../../definitions/FlyerDefinitions'
import { Coupon } from '../../definitions/CouponDefinitions'

export const enum CouponActions {
    Open,
    Barcode,
    Add, // Clip coupon
    AddMatched, // Autoclip from Clipping
    Send, // Fired when "Send" is tapped on coupon redemption (print) screen
    ShareUrl,
    Redeemed, // (e.g., when app finds out from Postcard that a coupon is redeemed)
    LCSelected, // (e.g. when user selects a loyalty card among the choices in the coupon details)
}

export class CouponSource extends StringEnum {
    static Flyer = new CouponSource('flyer')
    static ItemDetails = new CouponSource('item_details')
    static CouponListing = new CouponSource('coupon_listing')
    static CouponDetails = new CouponSource('coupon_details')
    static Clippings = new CouponSource('clippings')
    static SearchPage = new CouponSource('search')
    static ShoppingListSearch = new CouponSource('sl_search')
}

export class LoginSource extends StringEnum {
    static CouponPage = new LoginSource('coupon_page')
    static CouponDetails = new LoginSource('coupon_details')
    static Signin = new LoginSource('signin')
    static Signup = new LoginSource('signup')
    static FlyerItem = new LoginSource('flyer_item')
    static ShareDialog = new LoginSource('share_dialog')
    static ShoppingList = new LoginSource('shopping_list')
}

export enum LoyaltyCardSource {
    CouponDetails,
    FlyerItem,
    CouponPage,
}

export class ShoppingListAction extends StringEnum {
    static Add = new ShoppingListAction('add')
    static Edit = new ShoppingListAction('edit')
    static Delete = new ShoppingListAction('delete')
    static Check = new ShoppingListAction('check')
    static Uncheck = new ShoppingListAction('uncheck')
    static DeleteList = new ShoppingListAction('deleteList')
    static CleanupList = new ShoppingListAction('cleanUpList')
    static Print = new ShoppingListAction('print')
}

export class ShoppingListEventContext extends StringEnum {
    static List = new ShoppingListAction('list')
    static AdAdapted = new ShoppingListAction('adadapted')
    static LastWeek = new ShoppingListAction('last_week')
    static TwoWeeks = new ShoppingListAction('two_weeks')
    static Popular = new ShoppingListAction('popular')
}

export class FamilySyncAction extends StringEnum {
    static Divorce = new FamilySyncAction('collaborationDivorce')
    static Accept = new FamilySyncAction('collaborationAccept')
    static Open = new FamilySyncAction('collaborationClick')
    static Create = new FamilySyncAction('collaborationCreate')
}

export type FavouriteAction = 'Favorited' | 'Unfavorited'

export class FavouriteContext extends StringEnum {
    static Listing = new FavouriteContext('listing')
    static Search = new FavouriteContext('search')
    static Favourites = new FavouriteContext('favourites')
    static FlyerView = new FavouriteContext('flyerview')
}

// source_action parameter should represent the action which the user has taken
// to reach the flyer
// NOTE: feature_${x}_flyer_click is NOT a static string, so it cannot be
// declared here. It is a dynamic string, where ${x} is anything OTHER than
// "All Flyers" (e.g. "Groceries", "Home & Garden", etc.)
// ----------------------------------------------------------------------------
export type CategorySourceAction = string
export type AggregateSourceAction = SourceAction | CategorySourceAction

export function getCategorySourceActionValue(name: string) {
    return `feature_${name}_flyer_click` as CategorySourceAction
}

export function gtag(...args: any[]) {
    dataLayer.push(args)
}

export enum SourceAction {
    Direct = 'direct', // Getting directly to the flyer (e.g. arbitrage)
    CrossBrowse = 'cross_browse', // Click on the "Next Flyer" button
    BrowseFlyerClick = 'browse_flyer_click', // Click on ANY flyer (premium or not) AND the category is "All Flyers"
    // 								 "feature_${x}_flyer_click", 		// Click on ANY flyer (premium or not) AND the category is ${X}, where X is anything OTHER than "All Flyers" (e.g. "Groceries", "Home & Garden", etc.)
    ClippingsClick = 'clippings_click', // Click from clippings
    FavoriteClick = 'favorite_click', // Click from favourites
    SearchFlyerClick = 'search_flyer_click', // Click from flyer thumbnail from search
    SearchFlyerItemClick = 'search_flyer_item_click', // Click from flyer item from search
    StoreFlyerClick = 'store_flyer_click', // Click from flyer from store page
}

export class Dimension extends StringEnum {
    static MerchantName = new Dimension('dimension1')
    static MerchantId = new Dimension('dimension2')
    static FlyerId = new Dimension('dimension3')
    static SearchQuery = new Dimension('dimension4')
    static VersionTag = new Dimension('dimension5')
    static Crossbrowse = new Dimension('dimension6')
    static FlyerStatus = new Dimension('dimension8')
    static CouponType = new Dimension('dimension9')
    static CouponVendorId = new Dimension('dimension10')
    static CouponId = new Dimension('dimension11')
    static Brand = new Dimension('dimension12')
    static RedemptionMethod = new Dimension('dimension13')
    static ClickSource = new Dimension('dimension14')
    static Variant = new Dimension('dimension15')
    static Context = new Dimension('dimension16')
    static ProgramValidationType = new Dimension('dimension17')
    static LoyaltyProgramId = new Dimension('dimension18')
    static LoyaltyCouponId = new Dimension('dimension19')
    static ShoppingListId = new Dimension('dimension20')
    static ShoppingListContext = new Dimension('dimension21')
    static Referer = new Dimension('dimension22')
    static UserId = new Dimension('dimension23')
    static PublisherName = new Dimension('dimension24')
    static PublisherChannelId = new Dimension('dimension25')
}

export class Metric extends StringEnum {
    static SearchResult = new Metric('metric3')
    static Crossbrowse = new Metric('metric5')
}

export const enum LandingPageActions {
    StartSavingButtonClick,
    BrowseFlyersButtonClick,
    BrowseCouponsButtonClick,
    CreateSLButtonClick,
    FacebookButtonClick,
    TwitterButtonClick,
    YoutubeButtonClick,
    LinkedinButtonClick,
    FlyersNavClick,
    CouponsNavClick,
    SLNavClick,
    SearchNavClick,
    BackToTopClick,
    AboutFlippClick,
    FlyerOpen,
    BrowseFlippButtonClick,
    LearnMoreButtonClick,
    SearchFlippButtonClick,
    SeeMoreButtonClick,
    JobOpeningsButtonClick,
    JobOpeningsLinkClick,
}

export const enum CrossBrowseAction {
    NextFlyer,
    FlyerSuggestion,
    SeeAllFlyers,
    TopLogo,
}

export class FlyerPlacement extends StringEnum {
    static Promoted = new FlyerPlacement('Promoted')
    static Organic = new FlyerPlacement('Organic')
    static Popular = new FlyerPlacement('Popular')
}

export const LANDING_PAGE_VERSION = '2.0.0'

/* The root/base GA tracker. All user specific events (engagements, coupons, SL, etc) will be sent
 * to this tracker. This tracker may be linked to a different GA profile depending on if we're in
 * native/distribution runtime or default */
export const TRACKER_BASE = 'baseTracker'

/* The vendor GA tracker, which is used to send open/view/ev events to a distribution partners GA
 * profile. */
export const TRACKER_PARTNER = 'partnerTracker'

export interface IAnalyticsService {
    sessionId: string
    channelId: number
    profileId: string

    setPostalCode(postalCode: string): void
    setLocale(locale: string): void
    setAccountGuid(guid: string): void
    getAccountGuid(): string

    isAnalyticsDelayed(): boolean
    originFlyerId(): string
    siteLoaded(): void

    trackPageView(): void
    trackFlyerOpen(
        flyer: Flyer,
        sourceAction: AggregateSourceAction,
        xbrowseId?: string
    ): void
    trackFlyerEngagement(
        flyer: Flyer,
        sourceAction: AggregateSourceAction,
        xbrowseId?: string
    ): void
    trackFlyerView(
        flyer: Flyer,
        left: number,
        top: number,
        width: number,
        height: number,
        zoomScale: number,
        interaction: number
    ): void
    trackFlyerItem(
        flyer: Flyer,
        itemId: number,
        st: number,
        alternateId?: string
    ): void
    trackEcomItem(
        itemId: string,
        merchantId: number,
        merchant: string,
        st: number,
        isHidden: boolean
    ): void
    trackSearch(
        q: string,
        flyerCount: number,
        tombstoneCount: number,
        itemCount: number,
        ecomItemCount: number,
        couponCount: number,
        couponMatchupCount: number,
        mode: SearchMode
    ): void
    trackSearchFiltering(count: number, sortOrder: string): void
    trackAdditionalResultsClick(count: number): void
    trackTombstone(q: string, merchant: string, merchantId: number): void
    trackSearchItemClick(
        q: string,
        itemId: number,
        flyer: Flyer,
        rank: number
    ): void
    trackSearchFlyerClick(q: string, flyer: Flyer, rank: number): void
    trackSearchCouponClick(q: string, coupon: Coupon, rank: number): void
    trackSearchEcomItemClick(
        q: string,
        itemId: string,
        merchantId: number,
        isHidden: boolean,
        rank: number
    ): void
    trackCrossBrowse(
        type: CrossBrowseAction,
        from?: Flyer,
        to?: Flyer,
        idx?: number
    ): void
    trackMobileInstall(device: string, category: string): void
    trackCouponPrint(): void
    trackCoupon(
        coupon: Coupon,
        st: CouponActions,
        src: CouponSource,
        flyerId?: number,
        programMerchant?: LoyaltyProgramMerchant,
        loyaltyCoupon?: LoyaltyProgramCoupon
    ): void
    trackVariant(experiment: Experiment, value: string): void
    trackSignin(
        userGuid: string,
        provider: AuthProvider,
        context: LoginSource
    ): void
    trackSignout(userGuid: string, provider: AuthProvider): void
    trackWebGLDisabled(): void
    trackLoyaltyCardAdded(
        program: LoyaltyProgram,
        context: LoyaltyCardSource
    ): void
    trackLoyaltyCardRemoved(programId: number): void
    trackPageNotFoundEvent(): void
    trackShoppingListChanged(
        item: string,
        listId: string,
        action: ShoppingListAction,
        context: ShoppingListEventContext,
        numClippings?: string
    ): void
    trackShoppingListPhotoDeleted(listId: string): void
    trackShoppingListRecommendedStream(
        action: string,
        stream: string,
        listId: string,
        item: string
    ): void
    trackSearchMiss(item: string, listId: string): void
    trackAutocompleteHit(
        q: string,
        term: string,
        rank: number,
        context: string
    ): void
    trackShoppingListEvent(
        listId: string,
        action: ShoppingListAction,
        numAffected: number
    ): void
    trackShoppingListPrint(listId: string, action: ShoppingListAction): void
    trackFamilySyncEvent(
        listId: string,
        action: FamilySyncAction,
        inviteId?: string
    ): void
    trackFavouriteEvent(
        merchantId: number,
        action: FavouriteAction,
        context: FavouriteContext
    ): void
    trackOldBrowser(): void
    trackFlyerListing(
        categoryName: string,
        flyers: Flyer[],
        sortOrder: string,
        favMerchants?: number[]
    ): void
    trackPublisherBackButtonClick(publisherName: string): void
    trackPublisherBackButtonVisible(publisherName: string): void
    trackFlyerClick(
        category: string,
        sort: string,
        placement: FlyerPlacement,
        position: number
    ): void
    trackCouponListing(
        categoryName: string,
        coupons: Coupon[],
        sortOrder: string
    ): void
    trackLandingPage(st: LandingPageActions, q?: string, flyer?: Flyer): void
    trackUnsupportedBrowserErrorPage(): void
    trackFatalRuntimeErrorPage(): void
    trackLocationChanges(oldFsa: string, newFsa: string): void
    trackShoppingListView(
        items: number,
        photos: number,
        flyerItems: number,
        ecomItems: number,
        coupon: number
    ): void
    trackFlyerClose(flyer: Flyer, sourceAction: AggregateSourceAction): void
    trackWindowClose(): void
}

export interface TrackFlippProps {
    ev: string
    options: { [key: string]: any }
    force?: boolean
    syncGetRequest?: boolean
    fireTagman?: boolean
    addAvroKey?: boolean
    isAvro?: boolean
}
